import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../javascript/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../../javascript/components/ui/chart";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { formatLapTime } from "../../javascript/components/ui/lap-time";
import type { RaceParticipant, LapChartPosition } from "@/types/generated";

interface RaceIncident {
  participant_id: number;
  display_name: string;
  is_current_user: boolean;
  is_linked_friend?: boolean;
  lap_number: number;
  position_before: number;
  position_after: number;
  positions_lost: number;
}

interface PaceComparison {
  current_user: {
    participant_id: number;
    display_name: string;
    average_lap_time_ms: number;
    best_lap_time_ms: number;
    is_current_user: boolean;
  };
  p1: {
    participant_id: number;
    display_name: string;
    average_lap_time_ms: number;
    best_lap_time_ms: number;
  };
  gap: {
    average_gap_ms: number;
    best_lap_gap_ms: number;
    average_gap_seconds: number;
    best_lap_gap_seconds: number;
  };
  is_p1: boolean;
}

interface LapChartResponse {
  lap_chart: {
    total_laps: number;
    total_drivers: number;
    participants: RaceParticipant[];
    incidents?: RaceIncident[];
    pace_comparison?: PaceComparison | null;
  };
}

interface LapChartProps {
  trackEventId: string | number;
}

// Swiss design color palette - muted, sophisticated colors
const CHART_COLORS = [
  "#475569", // slate-600
  "#64748b", // slate-500
  "#94a3b8", // slate-400
  "#71717a", // zinc-500
  "#a1a1aa", // zinc-400
  "#737373", // neutral-500
  "#a3a3a3", // neutral-400
  "#78716c", // stone-500
  "#a8a29e", // stone-400
  "#ef4444", // red-500
  "#f97316", // orange-500
  "#f59e0b", // amber-500
  "#84cc16", // lime-500
  "#22c55e", // green-500
  "#14b8a6", // teal-500
  "#06b6d4", // cyan-500
  "#3b82f6", // blue-500
  "#6366f1", // indigo-500
  "#8b5cf6", // violet-500
  "#d946ef", // fuchsia-500
];

// Color for current user - refined green for Swiss design
const CURRENT_USER_COLOR = "#22c55e"; // green-500

export default function LapChart({ trackEventId }: LapChartProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartData, setChartData] = useState<LapChartResponse["lap_chart"] | null>(null);
  const [incidents, setIncidents] = useState<RaceIncident[]>([]);
  const [paceComparison, setPaceComparison] = useState<PaceComparison | null>(
    null
  );

  useEffect(() => {
    fetch(`/event/${trackEventId}/lap_chart`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load lap chart data");
        return res.json();
      })
      .then((data: LapChartResponse) => {
        setChartData(data.lap_chart);
        setIncidents(data.lap_chart.incidents || []);
        setPaceComparison(data.lap_chart.pace_comparison || null);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [trackEventId]);

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </CardContent>
      </Card>
    );
  }

  if (error || !chartData) {
    return (
      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="text-destructive">
            Unable to load lap chart
          </CardTitle>
          <CardDescription>{error || "No data available"}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (chartData.participants.length === 0) {
    return (
      <Card className="border-yellow-500">
        <CardHeader>
          <CardTitle>No lap chart data available</CardTitle>
          <CardDescription>
            Position data not available for this session
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  // Transform data for Recharts - include lap 0 for starting positions
  const lapNumbers = Array.from(
    { length: chartData.total_laps + 1 },
    (_, i) => i
  );
  const transformedData = lapNumbers.map((lapNum) => {
    const dataPoint: Record<string, number> = { lap: lapNum };

    chartData.participants.forEach((participant: RaceParticipant) => {
      const position = participant.lap_chart_positions.find(
        (p: LapChartPosition) => p.lap_number === lapNum
      );
      if (position) {
        dataPoint[participant.display_name] = position.position;
      }
    });

    return dataPoint;
  });

  // Build chart config with special color for current user
  const chartConfig = chartData.participants.reduce(
    (config: Record<string, { label: string; color: string }>, participant: RaceParticipant, index: number) => {
      config[participant.display_name] = {
        label: participant.display_name,
        color: participant.is_current_user
          ? CURRENT_USER_COLOR
          : CHART_COLORS[index % CHART_COLORS.length],
      };
      return config;
    },
    {} as Record<string, { label: string; color: string }>
  );

  // Calculate Y-axis ticks to include position 1 and user's start position
  const currentUserParticipant = chartData.participants.find(
    (p: RaceParticipant) => p.is_current_user
  );
  const userStartPosition =
    currentUserParticipant?.lap_chart_positions[0]?.position;
  const maxPosition = Math.max(
    ...chartData.participants.flatMap((p: RaceParticipant) =>
      p.lap_chart_positions.map((pos: LapChartPosition) => pos.position)
    )
  );

  // Generate tick values: always include 1 (first place), user's start, and other intervals
  const generateYAxisTicks = () => {
    const ticks = new Set<number>([1]); // Always include position 1

    if (userStartPosition) {
      ticks.add(userStartPosition);
    }

    // Add evenly spaced ticks
    const interval = Math.max(1, Math.floor(maxPosition / 8));
    for (let i = 1; i <= maxPosition; i += interval) {
      ticks.add(i);
    }

    // Always include the last position
    ticks.add(maxPosition);

    return Array.from(ticks).sort((a, b) => a - b);
  };

  const yAxisTicks = generateYAxisTicks();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Race Position Chart</CardTitle>
        <CardDescription>
          {chartData.total_drivers} drivers · {chartData.total_laps} laps
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <ChartContainer config={chartConfig} className="h-[400px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={transformedData}
              margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
            >
              <CartesianGrid
                strokeDasharray="0"
                stroke="hsl(var(--border))"
                strokeOpacity={0.2}
                vertical={false}
              />
              <XAxis
                dataKey="lap"
                label={{
                  value: "Lap Number",
                  position: "insideBottom",
                  offset: -10,
                  style: {
                    fontSize: "11px",
                    fill: "hsl(var(--muted-foreground))",
                  },
                }}
                tick={{
                  fontSize: 11,
                  fill: "hsl(var(--muted-foreground))",
                }}
                tickLine={{ stroke: "hsl(var(--border))" }}
                axisLine={{ stroke: "hsl(var(--border))" }}
              />
              <YAxis
                reversed
                label={{
                  value: "Position",
                  angle: -90,
                  position: "insideLeft",
                  style: {
                    fontSize: "11px",
                    fill: "hsl(var(--muted-foreground))",
                  },
                }}
                domain={[1, maxPosition]}
                ticks={yAxisTicks}
                tick={{
                  fontSize: 11,
                  fill: "hsl(var(--muted-foreground))",
                }}
                tickLine={{ stroke: "hsl(var(--border))" }}
                axisLine={{ stroke: "hsl(var(--border))" }}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              {chartData.participants.map((participant: RaceParticipant, index: number) => (
                <Line
                  key={participant.id}
                  type="stepAfter"
                  dataKey={participant.display_name}
                  stroke={
                    participant.is_current_user
                      ? CURRENT_USER_COLOR
                      : CHART_COLORS[index % CHART_COLORS.length]
                  }
                  strokeWidth={participant.is_current_user ? 3 : 1.5}
                  dot={false}
                  activeDot={{ r: participant.is_current_user ? 6 : 4 }}
                  opacity={participant.is_current_user ? 1 : 0.7}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>

        {/* Legend */}
        <div className="space-y-3 pt-4 border-t">
          {/* Current user */}
          {chartData.participants
            .filter((p: RaceParticipant) => p.is_current_user)
            .map((participant: RaceParticipant) => {
              const startingPosition = participant.lap_chart_positions.find(
                (p: LapChartPosition) => p.lap_number === 0
              )?.position;
              const finishingPosition = participant.lap_chart_positions.find(
                (p: LapChartPosition) => p.lap_number === chartData.total_laps
              )?.position;

              return (
                <div
                  key={participant.id}
                  className="flex items-center gap-3 p-2 bg-accent/50 border rounded"
                >
                  <div
                    className="w-3 h-3 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: CURRENT_USER_COLOR }}
                  />
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-sm">You</span>
                    <span className="text-xs text-muted-foreground ml-2 truncate">
                      {participant.display_name}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {startingPosition && finishingPosition && (
                      <span>
                        P{startingPosition} → P{finishingPosition}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}

          {/* Top finishers */}
          <div className="space-y-1">
            {chartData.participants
              .filter((p: RaceParticipant) => !p.is_current_user)
              .sort((a: RaceParticipant, b: RaceParticipant) => {
                const aFinish =
                  a.lap_chart_positions.find(
                    (p: LapChartPosition) => p.lap_number === chartData.total_laps
                  )?.position || 999;
                const bFinish =
                  b.lap_chart_positions.find(
                    (p: LapChartPosition) => p.lap_number === chartData.total_laps
                  )?.position || 999;
                return aFinish - bFinish;
              })
              .slice(0, 10)
              .map((participant: RaceParticipant) => {
                const finishingPosition = participant.lap_chart_positions.find(
                  (p: LapChartPosition) => p.lap_number === chartData.total_laps
                )?.position;
                const participantColorIndex = chartData.participants.findIndex(
                  (p: RaceParticipant) => p.id === participant.id
                );

                return (
                  <div
                    key={participant.id}
                    className="flex items-center gap-2 text-sm py-1"
                  >
                    <div
                      className="w-2 h-2 rounded-sm flex-shrink-0"
                      style={{
                        backgroundColor:
                          CHART_COLORS[
                          participantColorIndex % CHART_COLORS.length
                          ],
                      }}
                    />
                    <span className="truncate flex-1 min-w-0 flex items-center gap-1.5">
                      {participant.display_name}
                      {participant.is_linked_friend && (
                        <span className="inline-flex items-center rounded-sm bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                          Friend
                        </span>
                      )}
                    </span>
                    {finishingPosition && (
                      <span className="text-muted-foreground">
                        P{finishingPosition}
                      </span>
                    )}
                  </div>
                );
              })}
          </div>

          {chartData.participants.length > 11 && (
            <div className="text-sm text-muted-foreground text-center pt-2">
              +{chartData.participants.length - 11} more drivers
            </div>
          )}
        </div>

        {/* Pace Comparison to P1 */}
        {paceComparison && (
          <div className="pt-4 space-y-3 border-t">
            <div className="text-sm font-medium">
              {paceComparison.is_p1 ? "Race Winner" : "Pace Comparison"}
            </div>

            {paceComparison.is_p1 ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-muted-foreground mb-0.5">
                    Average Lap
                  </div>
                  <div className="text-lg font-semibold font-ocr">
                    {formatLapTime(
                      paceComparison.current_user.average_lap_time_ms
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground mb-0.5">
                    Best Lap
                  </div>
                  <div className="text-lg font-semibold font-ocr">
                    {formatLapTime(
                      paceComparison.current_user.best_lap_time_ms
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground mb-0.5">
                      {paceComparison.current_user.is_current_user ? "Your Average" : `${paceComparison.current_user.display_name}'s Average`}
                    </div>
                    <div className="text-lg font-semibold font-ocr">
                      {formatLapTime(
                        paceComparison.current_user.average_lap_time_ms
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      +{formatLapTime(paceComparison.gap.average_gap_ms)} vs P1
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground mb-0.5">
                      {paceComparison.current_user.is_current_user ? "Your Best" : `${paceComparison.current_user.display_name}'s Best`}
                    </div>
                    <div className="text-lg font-semibold font-ocr">
                      {formatLapTime(
                        paceComparison.current_user.best_lap_time_ms
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      +{formatLapTime(paceComparison.gap.best_lap_gap_ms)} vs P1
                    </div>
                  </div>
                </div>
                <div className="text-sm text-muted-foreground pt-2 border-t">
                  P1: {paceComparison.p1.display_name} · Best{" "}
                  {formatLapTime(paceComparison.p1.best_lap_time_ms)} · Avg{" "}
                  {formatLapTime(paceComparison.p1.average_lap_time_ms)}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Race Incidents */}
        {incidents.length > 0 && (
          <div className="pt-4 space-y-3 border-t">
            <div className="text-sm font-medium">
              Incidents ({incidents.length})
            </div>
            <div className="space-y-2">
              {incidents.map((incident, idx) => (
                <div
                  key={idx}
                  className={`flex items-center justify-between text-sm p-2 rounded border ${incident.is_current_user
                    ? "bg-destructive/10 border-destructive/30"
                    : "bg-accent/50"
                    }`}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="font-medium truncate flex items-center">
                      {incident.is_current_user ? "You" : incident.display_name}
                      {incident.is_linked_friend && (
                        <span className="ml-2 inline-flex items-center rounded-sm bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                          Friend
                        </span>
                      )}
                    </span>
                    <span className="text-muted-foreground">
                      Lap {incident.lap_number}
                    </span>
                  </div>
                  <div className="text-muted-foreground whitespace-nowrap ml-2">
                    P{incident.position_before} → P{incident.position_after}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
